import startCase from 'lodash/startCase'
import {
  IShipperApplicationContact,
  Maybe,
  Scalars,
  IShipperApplicationContactRoles,
  IShipmentAccessorialNames,
} from 'types/graphqlTypes'
import { IEdiContactRoles } from '../../ShipperEdi/components/EdiContactsCard/schemas/EdiContactsSchema'
import { IShipperContactsQuery } from '../graphql/ShipperContacts'

const ACCESSORIAL_NAME_MAP: Partial<Record<IShipmentAccessorialNames, string>> = {
  driver_loading_unloading: 'Driver Loading/Unloading',
  es_escort_service_fee: 'ES-Escort Service Fee',
  financial_reconciliation__for_ar_ap_use_only_: 'Financial Reconciliation (For AR/AP Use Only)',
  otp_otd_late_fee: 'OTP/OTD Late Fee',
  redelivery___refused_goods: 'Redelivery / Refused Goods',
  stop_off_charge: 'Stop-off Charge',
  tonu: 'TONU',
}

export interface IShipperContact extends Omit<IShipperApplicationContact, 'id' | 'role'> {
  ediEmails?: Maybe<
    {
      email: string
      name: string
    }[]
  >
  caption?: string
  role:
    | 'Primary'
    | 'Accounting'
    | 'Accessorials'
    | 'EDI Connections'
    | IShipperApplicationContactRoles
    | IEdiContactRoles
  id?: Scalars['ID']
}

export interface IEdiContact extends Omit<IShipperApplicationContact, 'id' | 'role'> {
  ediEmails?: Maybe<
    {
      email: string
      name: string
    }[]
  >
  caption?: string
  role: IEdiContactRoles
  id?: Scalars['ID']
}

export const getEdiContacts = (
  contacts: IShipperApplicationContact[] | undefined
): IShipperContact[] => {
  if (!contacts) return []

  const ediContactRoles = Object.values(IEdiContactRoles)
  return contacts.filter(contact => ediContactRoles.includes(contact.role))
}

/**
 * Normalizes the Primary (Shipper), Operations, Accounting (Billing), Accessorials, and EDI Connections contacts from shipperApplication data to display in Contacts card
 */
export const normalizeShipperContacts = (
  contactData: IShipperContactsQuery | undefined
): IShipperContact[] => {
  if (!contactData) return []
  const {
    accessorialPolicies,
    accountingEmail,
    contacts,
    headquarter,
    secondaryAccountingEmail,
    shipmentEmail,
  } = contactData?.shipperApplication || {}

  const {
    contactName: accountingContactName,
    contactPhone: accountingContactPhone,
    contactPhoneExt: accountingContactPhoneExt,
  } = headquarter || {}

  const ediContacts = getEdiContacts(contacts)

  const accessorialContacts: IShipperContact[] =
    accessorialPolicies?.map(policy => ({
      role: 'Accessorials',
      email: policy.approvalEmail,
      name:
        ACCESSORIAL_NAME_MAP[policy.shipmentAccessorialName] ||
        startCase(policy.shipmentAccessorialName),
      caption: policy?.equipment?.name || '',
    })) || []

  const primaryShipperContact: IShipperContact = { role: 'Primary', email: shipmentEmail }

  const accountingContact: IShipperContact = {
    role: 'Accounting',
    email: accountingEmail,
    secondaryEmail: secondaryAccountingEmail,
    phone: accountingContactPhone,
    phoneExtension: accountingContactPhoneExt,
    name: accountingContactName,
  }

  return [primaryShipperContact, accountingContact, ...accessorialContacts, ...ediContacts].filter(
    contact => contact.phone || contact.email || contact.ediEmails
  )
}
